import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { RootState } from '../../store';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { usePersistentState } from '../../utils/usePersistentState';
import { showAlert, showConfirm } from '../common/AppDialog';
import { fetchTableColumns } from '../../utils/tableService';
import { formatDateTime } from '../../utils/formatters';
import SpecGrid, { GridCol } from './SpecGrid';
import {
  DictColumn, MappingSpec, MODES, RULES, Sheet, SpecCode, SpecColumn, SpecIssue, SpecTable,
  checkAgainstDb, checkExprs, checkSpec, downloadTemplate, emptyCode, emptyColumn, emptyTable, exportSheet,
  importCsv, loadSpec, ruleToItem, saveSpec,
} from '../../services/mappingSpec';

type Body = Pick<MappingSpec, 'tables' | 'columns' | 'codes'>;
interface Draft { body: Body; baseVersion: number; savedAt: string | null; savedBy: string; dirty: boolean }
type Tab = 'map' | 'codes' | 'issues';

const dictKey = (conn: string, table: string) => `${conn.trim()}\u0000${table.trim().toUpperCase()}`;

/**
 * 매핑 설계서 — 데이터 이행·ETL 의 테이블 매핑 · 컬럼 매핑 · 코드 매핑 (services/mappingSpec).
 *
 * 위: 테이블 매핑(매핑ID 마다 소스 → 타깃), 아래: 고른 매핑의 컬럼 매핑. 코드 매핑은 탭으로.
 * 그리드에서 바로 쓰거나, 양식 CSV 를 내려받아 채운 뒤 가져온다(파일에 있는 매핑ID·코드그룹은 그 내용으로 바뀐다).
 * 저장하면 판이 오른다. 변환및적재 노드는 판을 정해 매핑을 가져가므로, 저장만으로 배포본이 바뀌지 않는다.
 *
 * 닫았다 열면 저장 전 내용·고른 매핑·탭이 그대로다(usePersistentState).
 */
const MappingSpecDialog: React.FC<{ projectId: string; onClose: () => void }> = ({ projectId, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const project = useSelector((s: RootState) => s.project.projects.find((p) => p.id === projectId));
  const connections = useSelector((s: RootState) => s.connections.list);
  const connIds = useMemo(() => connections.map((c: any) => c.connection_id).filter(Boolean) as string[], [connections]);

  const [draft, setDraft] = usePersistentState<Draft | null>(`mapspec:${projectId}:draft`, null);
  const [tab, setTab] = usePersistentState<Tab>(`mapspec:${projectId}:tab`, 'map');
  const [selId, setSelId] = usePersistentState<string>(`mapspec:${projectId}:sel`, '');
  const [issues, setIssues] = usePersistentState<SpecIssue[] | null>(`mapspec:${projectId}:issues`, null);
  const [codeFilter, setCodeFilter] = usePersistentState<string>(`mapspec:${projectId}:codeFilter`, '');
  const [busy, setBusy] = useState<string>('');
  const dict = useRef(new Map<string, DictColumn[] | null>());
  const [, setDictTick] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => onClose(), [onClose]);
  useEscapeKey(close);

  const reload = useCallback(async () => {
    setBusy('load');
    try {
      const s = await loadSpec(projectId);
      setDraft({ body: { tables: s.tables, columns: s.columns, codes: s.codes }, baseVersion: s.version, savedAt: s.savedAt, savedBy: s.savedBy, dirty: false });
      setIssues(null);
    } catch (e: any) {
      showAlert(t('mapping:loadFailed', { message: e?.message || String(e) }));
    } finally {
      setBusy('');
    }
  }, [projectId, setDraft, setIssues, t]);
  useEffect(() => { if (!draft) reload(); }, [draft, reload]);

  const body: Body = draft?.body || { tables: [], columns: [], codes: [] };
  const edit = useCallback((fn: (b: Body) => Body) => {
    setDraft((d) => (d ? { ...d, body: fn(d.body), dirty: true } : d));
  }, [setDraft]);

  const selTable = body.tables.find((x) => x.mappingId.trim() && x.mappingId.trim() === selId.trim());
  useEffect(() => {
    if (!selTable && body.tables.length && body.tables[0].mappingId.trim()) setSelId(body.tables[0].mappingId);
  }, [selTable, body.tables, setSelId]);

  // ── 사전 (컬럼 자료형·NOT NULL·길이)
  const loadDict = useCallback(async (pairs: Array<[string, string]>) => {
    const todo = pairs.filter(([c, tb]) => c.trim() && tb.trim() && !dict.current.has(dictKey(c, tb)));
    await Promise.all(todo.map(async ([c, tb]) => {
      try {
        const r = await fetchTableColumns(c.trim(), tb.trim());
        dict.current.set(dictKey(c, tb), (r.columns || []) as DictColumn[]);
      } catch {
        dict.current.set(dictKey(c, tb), null);
      }
    }));
    if (todo.length) setDictTick((n) => n + 1);
  }, []);
  useEffect(() => {
    if (selTable) loadDict([[selTable.tgtConn, selTable.tgtTable], [selTable.srcConn, selTable.srcTable]]);
  }, [selTable, loadDict]);

  // ── 검증
  const validate = async () => {
    setBusy('check');
    try {
      dict.current.clear();
      await loadDict(body.tables.flatMap((x) => [[x.tgtConn, x.tgtTable], [x.srcConn, x.srcTable]] as Array<[string, string]>));
      const found = [...checkSpec(body), ...checkAgainstDb(body, dict.current)];
      const exprOf = body.columns.map((c) => ruleToItem(c, body.codes).item?.expr || '');
      const errs = await checkExprs(exprOf);
      exprOf.forEach((e, row) => {
        if (e && errs[e]) found.push({ sheet: 'columns', row, level: 'error', code: 'badExpr', params: { col: body.columns[row].target, message: errs[e] } });
      });
      setIssues(found);
      setTab(found.length ? 'issues' : tab);
      if (!found.length) showAlert(t('mapping:check.ok'));
    } catch (e: any) {
      showAlert(t('mapping:check.failed', { message: e?.message || String(e) }));
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    if (!draft) return;
    const errors = checkSpec(body).filter((i) => i.level === 'error');
    if (errors.length && !(await showConfirm(t('mapping:save.withErrors', { n: errors.length })))) return;
    setBusy('save');
    try {
      const s = await saveSpec(projectId, body, draft.baseVersion);
      setDraft({ body: { tables: s.tables, columns: s.columns, codes: s.codes }, baseVersion: s.version, savedAt: s.savedAt, savedBy: s.savedBy, dirty: false });
      showAlert(s.version === draft.baseVersion ? t('mapping:save.same', { v: s.version }) : t('mapping:save.done', { v: s.version }));
    } catch (e: any) {
      showAlert(t('mapping:save.failed', { message: e?.message || String(e) }));
    } finally {
      setBusy('');
    }
  };

  const discard = async () => {
    if (draft?.dirty && !(await showConfirm(t('mapping:reloadConfirm')))) return;
    reload();
  };

  // ── 가져오기: 파일에 있는 매핑ID(테이블·컬럼)·코드그룹은 그 내용으로 바꾸고 나머지는 둔다
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy('import');
    try {
      const r = await importCsv(projectId, Array.from(files));
      const lines: string[] = [];
      let next = body;
      r.sheets.forEach((s) => {
        if (s.sheet === 'tables') {
          const rows = s.rows as SpecTable[];
          const ids = new Set(rows.map((x) => x.mappingId.trim()));
          next = { ...next, tables: [...next.tables.filter((x) => !ids.has(x.mappingId.trim())), ...rows] };
          lines.push(t('mapping:import.tables', { name: s.name, n: rows.length }));
        } else if (s.sheet === 'columns') {
          const rows = s.rows as SpecColumn[];
          const ids = new Set(rows.map((x) => x.mappingId.trim()));
          next = { ...next, columns: [...next.columns.filter((x) => !ids.has(x.mappingId.trim())), ...rows] };
          lines.push(t('mapping:import.columns', { name: s.name, n: rows.length, m: ids.size }));
        } else {
          const rows = s.rows as SpecCode[];
          const groups = new Set(rows.map((x) => x.group.trim()));
          next = { ...next, codes: [...next.codes.filter((x) => !groups.has(x.group.trim())), ...rows] };
          lines.push(t('mapping:import.codes', { name: s.name, n: rows.length, m: groups.size }));
        }
        if (s.unknownHeaders.length) lines.push(t('mapping:import.unknownHeaders', { name: s.name, cols: s.unknownHeaders.join(', ') }));
      });
      r.errors.forEach((er) => lines.push(t(`error:${er.code}`, { ...er.params, defaultValue: er.code }) as string));
      if (r.sheets.length) {
        const fixed = next;
        edit(() => fixed);
        setIssues(null);
      }
      showAlert(<div className="whitespace-pre-line">{lines.join('\n')}</div>, { title: t('mapping:import.title') });
    } catch (e: any) {
      showAlert(t('mapping:import.failed', { message: e?.message || String(e) }));
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const exportAll = async () => {
    try {
      for (const sh of ['tables', 'columns', 'codes'] as Sheet[]) await exportSheet(sh, body[sh], lang);
    } catch (e: any) {
      showAlert(t('mapping:exportFailed', { message: e?.message || String(e) }));
    }
  };

  // ── DB 에서 채우기: 고른 매핑의 타깃 컬럼을 사전에서 늘어놓고, 소스에 같은 이름이 있으면 직접으로 잇는다
  const fillFromDb = async () => {
    if (!selTable) return;
    setBusy('fill');
    try {
      await loadDict([[selTable.tgtConn, selTable.tgtTable], [selTable.srcConn, selTable.srcTable]]);
      const tgt = dict.current.get(dictKey(selTable.tgtConn, selTable.tgtTable));
      if (!tgt?.length) { showAlert(t('mapping:fill.noTarget', { table: selTable.tgtTable })); return; }
      const src = dict.current.get(dictKey(selTable.srcConn, selTable.srcTable)) || [];
      const id = selTable.mappingId.trim();
      const have = new Set(body.columns.filter((c) => c.mappingId.trim() === id).map((c) => c.target.trim().toUpperCase()));
      const add = tgt.filter((c) => !have.has(c.name.toUpperCase())).map((c) => {
        const s = src.find((x) => x.name.toUpperCase() === c.name.toUpperCase());
        return { ...emptyColumn(id), target: c.name, source: s?.name || '' };
      });
      if (!add.length) { showAlert(t('mapping:fill.nothing')); return; }
      edit((b) => ({ ...b, columns: [...b.columns, ...add] }));
      showAlert(t('mapping:fill.done', { n: add.length, matched: add.filter((x) => x.source).length }));
    } finally {
      setBusy('');
    }
  };

  // ── 그리드 정의
  const ruleOptions = RULES.map((r) => ({ value: r, label: t(`mapping:rule.${r}`) }));
  const tableCols: GridCol<SpecTable>[] = [
    { key: 'mappingId', label: t('mapping:col.mappingId'), width: 80 },
    { key: 'srcConn', label: t('mapping:col.srcConn'), width: 110, list: connIds },
    { key: 'srcTable', label: t('mapping:col.srcTable'), width: 150 },
    { key: 'where', label: t('mapping:col.where'), width: 150, mono: true },
    { key: 'tgtConn', label: t('mapping:col.tgtConn'), width: 110, list: connIds },
    { key: 'tgtTable', label: t('mapping:col.tgtTable'), width: 150 },
    { key: 'mode', label: t('mapping:col.mode'), width: 90, options: MODES.map((m) => ({ value: m, label: t(`mapping:mode.${m}`) })) },
    { key: 'order', label: t('mapping:col.order'), width: 50 },
    { key: 'note', label: t('mapping:col.note'), width: 120 },
  ];
  const srcDict = selTable ? dict.current.get(dictKey(selTable.srcConn, selTable.srcTable)) : undefined;
  const tgtDict = selTable ? dict.current.get(dictKey(selTable.tgtConn, selTable.tgtTable)) : undefined;
  const columnCols: GridCol<SpecColumn>[] = [
    { key: 'target', label: t('mapping:col.target'), width: 130, list: (tgtDict || []).map((c) => c.name) },
    { key: 'source', label: t('mapping:col.source'), width: 130, list: (srcDict || []).map((c) => c.name) },
    { key: 'rule', label: t('mapping:col.rule'), width: 80, options: ruleOptions },
    { key: 'value', label: t('mapping:col.value'), width: 220, mono: true, list: Array.from(new Set(body.codes.map((c) => c.group.trim()).filter(Boolean))) },
    { key: 'note', label: t('mapping:col.note'), width: 120 },
  ];
  const codeCols: GridCol<SpecCode>[] = [
    { key: 'group', label: t('mapping:col.group'), width: 120 },
    { key: 'from', label: t('mapping:col.from'), width: 120, mono: true },
    { key: 'to', label: t('mapping:col.to'), width: 120, mono: true },
    { key: 'note', label: t('mapping:col.note'), width: 200 },
  ];
  const describeTarget = (c: SpecColumn) => {
    const d = tgtDict?.find((x) => x.name.toUpperCase() === c.target.trim().toUpperCase());
    if (!d) return '';
    return `${d.dataType || ''}${d.length ? `(${d.length}${d.lengthUnit === 'CHAR' ? ' CHAR' : ''})` : ''}${d.nullable === false ? ' NN' : ''}`;
  };

  const marksOf = (sheet: Sheet) => {
    const m = new Map<number, 'error' | 'warn'>();
    (issues || []).filter((i) => i.sheet === sheet).forEach((i) => { if (m.get(i.row) !== 'error') m.set(i.row, i.level); });
    return m;
  };
  const tableRows = body.tables.map((row, index) => ({ row, index }));
  const columnRows = body.columns.map((row, index) => ({ row, index })).filter((x) => selTable && x.row.mappingId.trim() === selTable.mappingId.trim());
  const codeRows = body.codes.map((row, index) => ({ row, index })).filter((x) => !codeFilter.trim() || x.row.group.includes(codeFilter.trim()));

  const patchRow = <K extends Sheet>(sheet: K) => (index: number, p: Partial<Body[K][number]>) => {
    edit((b) => {
      const list = [...b[sheet]] as any[];
      const prev = list[index];
      list[index] = { ...prev, ...p };
      // 테이블의 매핑ID 를 바꾸면 그 매핑의 컬럼도 따라간다
      if (sheet === 'tables' && p && 'mappingId' in p && prev?.mappingId?.trim()) {
        const from = prev.mappingId.trim();
        const to = String((p as any).mappingId);
        const taken = b.tables.some((x, i) => i !== index && x.mappingId.trim() === from);
        if (!taken) {
          if (selId.trim() === from) setSelId(to);
          return { ...b, tables: list, columns: b.columns.map((c) => (c.mappingId.trim() === from ? { ...c, mappingId: to } : c)) };
        }
      }
      return { ...b, [sheet]: list };
    });
  };
  const appendRows = <K extends Sheet>(sheet: K, make: () => Body[K][number]) => (count: number) => {
    const start = body[sheet].length;
    edit((b) => ({ ...b, [sheet]: [...b[sheet], ...Array.from({ length: count }, make)] }));
    return Array.from({ length: count }, (_, i) => start + i);
  };
  const removeRow = (sheet: Sheet) => (index: number) => {
    edit((b) => {
      if (sheet === 'tables') {
        const id = b.tables[index]?.mappingId.trim();
        const others = b.tables.some((x, i) => i !== index && x.mappingId.trim() === id);
        return { ...b, tables: b.tables.filter((_, i) => i !== index), columns: id && !others ? b.columns.filter((c) => c.mappingId.trim() !== id) : b.columns };
      }
      return { ...b, [sheet]: (b[sheet] as any[]).filter((_, i) => i !== index) };
    });
    setIssues(null);
  };
  const newMappingId = () => {
    const nums = body.tables.map((x) => /^M(\d+)$/.exec(x.mappingId.trim())?.[1]).filter(Boolean).map(Number);
    return `M${String((nums.length ? Math.max(...nums) : 0) + 1).padStart(3, '0')}`;
  };

  const gotoIssue = (i: SpecIssue) => {
    if (i.sheet === 'codes') { setCodeFilter(''); setTab('codes'); }
    else {
      setTab('map');
      const id = i.sheet === 'tables' ? body.tables[i.row]?.mappingId : body.columns[i.row]?.mappingId;
      if (id) setSelId(id);
    }
    setTimeout(() => document.querySelector(`[data-row="${i.sheet}-${i.row}"]`)?.scrollIntoView({ block: 'center' }), 50);
  };
  const issueRowLabel = (i: SpecIssue) => {
    if (i.sheet === 'tables') return `${t('mapping:sheet.tables')} · ${body.tables[i.row]?.mappingId || `#${i.row + 1}`}`;
    if (i.sheet === 'columns') { const c = body.columns[i.row]; return `${t('mapping:sheet.columns')} · ${c?.mappingId || '?'} · ${c?.target || `#${i.row + 1}`}`; }
    return `${t('mapping:sheet.codes')} · ${body.codes[i.row]?.group || `#${i.row + 1}`}`;
  };
  const nErr = (issues || []).filter((i) => i.level === 'error').length;
  const nWarn = (issues || []).filter((i) => i.level === 'warn').length;

  const btn = 'px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={close}>
      <div className="bg-white rounded shadow-lg w-[1280px] max-w-[97vw] h-[88vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        {/* 머리 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">{t('mapping:title')}</div>
            <div className="text-xs text-gray-500 truncate">
              {project?.name || projectId} · {draft?.baseVersion ? t('mapping:version', { v: draft.baseVersion, at: formatDateTime(draft.savedAt), by: draft.savedBy || '-' }) : t('mapping:neverSaved')}
              {draft?.dirty && <span className="ml-2 text-amber-600">{t('mapping:dirty')}</span>}
            </div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={close} title={t('common:ui.close')}>✕</button>
        </div>

        {/* 도구 줄 */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-gray-100">
          <span className="text-xs text-gray-500">{t('mapping:template')}</span>
          {(['tables', 'columns', 'codes'] as Sheet[]).map((sh) => (
            <button key={sh} type="button" className={btn} onClick={() => downloadTemplate(sh, lang).catch((e) => showAlert(String(e?.message || e)))}>
              {t(`mapping:sheet.${sh}`)}
            </button>
          ))}
          <span className="mx-1 h-4 border-l border-gray-200" />
          <button type="button" className={btn} disabled={!!busy} onClick={() => fileRef.current?.click()} title={t('mapping:import.hint')}>{t('mapping:import.button')}</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <button type="button" className={btn} disabled={!!busy} onClick={exportAll}>{t('mapping:export')}</button>
          <span className="mx-1 h-4 border-l border-gray-200" />
          <button type="button" className={btn} disabled={!!busy} onClick={validate}>{busy === 'check' ? t('mapping:check.running') : t('mapping:check.button')}</button>
          <button type="button" className={btn} disabled={!!busy} onClick={discard}>{t('mapping:reload')}</button>
          <div className="flex-1" />
          <button type="button" className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!!busy || !draft} onClick={save}>
            {busy === 'save' ? t('mapping:save.running') : t('common:ui.save')}
          </button>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 px-4 pt-2 border-b border-gray-200 text-xs">
          {(['map', 'codes', 'issues'] as Tab[]).map((k) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={classNames('px-3 py-1.5 rounded-t border border-b-0', tab === k ? 'bg-white border-gray-200 text-blue-700 font-semibold -mb-px' : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-800')}>
              {k === 'map' ? t('mapping:tab.map') : k === 'codes' ? t('mapping:tab.codes', { n: body.codes.length })
                : <>{t('mapping:tab.issues')}{issues && <span className={classNames('ml-1', nErr ? 'text-red-600' : nWarn ? 'text-amber-600' : 'text-green-700')}>{nErr || nWarn ? `${nErr}/${nWarn}` : '✓'}</span>}</>}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 p-3">
          {!draft ? (
            <div className="text-xs text-gray-400 p-4">{t('mapping:loading')}</div>
          ) : tab === 'map' ? (
            <div className="h-full flex flex-col gap-3 min-h-0">
              <div className="h-[38%] min-h-0 flex flex-col">
                <div className="text-xs font-semibold text-gray-600 mb-1">{t('mapping:sheet.tables')} <span className="font-normal text-gray-400">({body.tables.length})</span></div>
                <div className="flex-1 min-h-0">
                  <SpecGrid id="tables" cols={tableCols} rows={tableRows} marks={marksOf('tables')}
                    selected={selTable ? body.tables.indexOf(selTable) : null}
                    onSelect={(i) => setSelId(body.tables[i]?.mappingId || '')}
                    onChange={patchRow('tables')} onRemove={removeRow('tables')}
                    onAdd={() => { const id = newMappingId(); edit((b) => ({ ...b, tables: [...b.tables, { ...emptyTable(), mappingId: id }] })); setSelId(id); }}
                    onAppend={appendRows('tables', emptyTable)}
                    empty={t('mapping:empty.tables')} />
                </div>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-xs font-semibold text-gray-600">
                    {t('mapping:sheet.columns')}
                    {selTable && <span className="font-normal text-gray-500"> — {selTable.mappingId} · {selTable.srcTable || '?'} → {selTable.tgtTable || '?'} ({columnRows.length})</span>}
                  </div>
                  <div className="flex-1" />
                  {selTable && tgtDict === null && <span className="text-[0.6875rem] text-amber-600">{t('mapping:dictUnreadable')}</span>}
                  <button type="button" className={btn} disabled={!selTable || !!busy || !selTable.tgtConn.trim() || !selTable.tgtTable.trim()} onClick={fillFromDb} title={t('mapping:fill.hint')}>
                    {busy === 'fill' ? t('mapping:fill.running') : t('mapping:fill.button')}
                  </button>
                </div>
                <div className="flex-1 min-h-0">
                  {selTable ? (
                    <SpecGrid id="columns" cols={columnCols} rows={columnRows} marks={marksOf('columns')}
                      onChange={patchRow('columns')} onRemove={removeRow('columns')}
                      onAdd={() => edit((b) => ({ ...b, columns: [...b.columns, emptyColumn(selTable.mappingId)] }))}
                      onAppend={appendRows('columns', () => emptyColumn(selTable.mappingId))}
                      extra={{ label: t('mapping:col.targetType'), width: 120, render: describeTarget }}
                      empty={t('mapping:empty.columns')} />
                  ) : (
                    <div className="text-xs text-gray-400 p-4">{t('mapping:empty.pickTable')}</div>
                  )}
                </div>
                <div className="mt-1 text-[0.6875rem] text-gray-500">{t('mapping:ruleHint')}</div>
              </div>
            </div>
          ) : tab === 'codes' ? (
            <div className="h-full flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-1">
                <input className="border border-gray-300 rounded px-2 py-0.5 text-xs w-48" value={codeFilter} placeholder={t('mapping:codeFilter')}
                  onChange={(e) => setCodeFilter(e.target.value)} />
                <span className="text-[0.6875rem] text-gray-500">{t('mapping:codeHint')}</span>
              </div>
              <div className="flex-1 min-h-0">
                <SpecGrid id="codes" cols={codeCols} rows={codeRows} marks={marksOf('codes')}
                  onChange={patchRow('codes')} onRemove={removeRow('codes')}
                  onAdd={() => edit((b) => ({ ...b, codes: [...b.codes, emptyCode(codeFilter.trim())] }))}
                  onAppend={appendRows('codes', () => emptyCode(codeFilter.trim()))}
                  empty={t('mapping:empty.codes')} />
              </div>
            </div>
          ) : (
            <div className="h-full overflow-auto">
              {!issues ? (
                <div className="text-xs text-gray-400 p-4">{t('mapping:check.notYet')}</div>
              ) : !issues.length ? (
                <div className="text-xs text-green-700 p-4">{t('mapping:check.ok')}</div>
              ) : (
                <table className="text-xs w-full">
                  <tbody>
                    {issues.map((i, k) => (
                      <tr key={k} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => gotoIssue(i)}>
                        <td className={classNames('px-2 py-1 w-6', i.level === 'error' ? 'text-red-600' : 'text-amber-600')}>{i.level === 'error' ? '✕' : '!'}</td>
                        <td className="px-2 py-1 whitespace-nowrap text-gray-500">{issueRowLabel(i)}</td>
                        <td className="px-2 py-1 text-gray-800">{t(`mapping:issue.${i.code}`, i.params || {})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MappingSpecDialog;
